/* eslint-disable */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Stack,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { MarkdownEditor } from './MarkdownEditor';
import { SlackPreview } from './SlackPreview';
import { SlackRecipients } from './SlackRecipients';
import { SendSlackButton } from './SendSlackButton';
import {
  getReportTemplate,
  type ReportTemplateRequest,
} from '../../services/api';

interface ReportEditorProps {
  initialRelease?: string;
}

export const ReportEditor: React.FC<ReportEditorProps> = ({
  initialRelease = '',
}) => {
  const [release, setRelease] = useState(initialRelease);
  const [markdownContent, setMarkdownContent] = useState('');
  const [channel, setChannel] = useState<string | undefined>();
  const [user, setUser] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadTemplate = async (releaseValue: string) => {
    if (!releaseValue.trim()) {
      setError('Please enter a release identifier');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const request: ReportTemplateRequest = {
        release: releaseValue,
      };
      const response = await getReportTemplate(request);
      setMarkdownContent(response.markdown_template);
      setSuccess('Template loaded successfully');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to load template. Please check the release identifier.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLoadTemplate = () => {
    loadTemplate(release);
  };

  const handleSendSuccess = () => {
    setSuccess('Report sent to Slack successfully!');
    setError(null);
  };

  const handleSendError = (errorMessage: string) => {
    setError(errorMessage);
    setSuccess(null);
  };

  // Auto-load template if initialRelease is provided
  useEffect(() => {
    if (initialRelease) {
      loadTemplate(initialRelease);
    }
  }, []);

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 2.5 }}>
        <Grid container spacing={2} alignItems="flex-start" sx={{ mx: 0, width: '100%' }}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Release"
              value={release}
              onChange={(e) => setRelease(e.target.value)}
              placeholder="26.01"
              helperText="Release identifier in format YY.MM"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              onClick={handleLoadTemplate}
              disabled={loading || !release.trim()}
              fullWidth
              sx={{ minHeight: 56 }}
            >
              {loading ? 'Loading...' : 'Load Template'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {(error || success) && (
        <Stack spacing={1.5}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}
        </Stack>
      )}

      <Grid container spacing={2} sx={{ mx: 0, width: '100%' }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.5, height: 600, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>
              Markdown Editor
            </Typography>
            <Box sx={{ flex: 1, mt: 1 }}>
              <MarkdownEditor
                value={markdownContent}
                onChange={setMarkdownContent}
                placeholder="Load a template or start typing markdown..."
              />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.5, height: 600, display: 'flex', flexDirection: 'column' }}>
            <SlackPreview markdownContent={markdownContent} />
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2.5 }}>
        <SlackRecipients
          channel={channel}
          user={user}
          onChannelChange={setChannel}
          onUserChange={setUser}
        />
      </Paper>

      <Paper sx={{ p: 2.5 }}>
        <SendSlackButton
          markdownContent={markdownContent}
          channel={channel}
          user={user}
          release={release}
          onSuccess={handleSendSuccess}
          onError={handleSendError}
        />
      </Paper>
    </Stack>
  );
};
